import React, { useState } from 'react';
import { 
  Box, 
  Button, 
  TextField, 
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';

// Styled components for custom styling
const StyledContactForm = Box;
const StyledContactInfo = Box;
const StyledContactFormSection = Box;

// Custom styled TextField to hide asterisk but keep required validation and proper label behavior
const CustomTextField = styled(TextField)({
  '& .MuiInputLabel-asterisk': {
    display: 'none',
  },
  '& .MuiInputLabel-root': {
    position: 'relative',
    transform: 'none',
    marginBottom: '8px',
    '&.Mui-focused, &.MuiFormLabel-filled': {
      transform: 'none',
    }
  },
  '& .MuiOutlinedInput-root': {
    marginTop: 0,
  }
});

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });

  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[0-9+\-\s()]*$/.test(formData.phone)) {
      newErrors.phone = 'Invalid phone number';
    }
    if (!formData.message.trim()) newErrors.message = 'Message is required';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      console.log('Form submitted:', formData);
      // Here you would typically send the form data to your backend
    }
  };

  return (
    <Box sx={{ 
      position: 'relative',
      width: '100%',
      minHeight: '100vh',
      p: { xs: 2, sm: 4 },
      backgroundColor: '#fafafa',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden',
    }}>
      <StyledContactForm sx={{
        width: '100%',
        maxWidth: '820px',
        backgroundColor: '#fff',
        borderRadius: '4px',
        boxShadow: '0 0 20px 1px rgba(0, 0, 0, 0.1)',
        zIndex: 1000,
        overflow: 'hidden',
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
        mt: { xs: 8, sm: 10, md: 12 },
        mb: { xs: 4, sm: 6, md: 8 },
      }}>
        {/* Contact Info Section */}
        <StyledContactInfo sx={{ 
          p: { xs: '1.7rem 1.6rem', md: '2.3rem 2.2rem' },
          backgroundColor: '#fff',
          position: 'relative',
          '&:before': {
            content: '""',
            position: 'absolute',
            width: '110px',
            height: '100px',
            border: '22px solid #1976d2',
            borderRadius: '50%',
            bottom: { xs: 'initial', md: '-77px' },
            top: { xs: '-75px', md: 'initial' },
            right: { xs: '65px', md: '50px' },
            opacity: 0.3,
            transform: { xs: 'scale(0.95)', md: 'initial' },
            display: { xs: 'none', sm: 'block' }, // Hide on smallest mobile
          },
        }}>
          <Typography variant="h4" sx={{ 
            color: '#1976d2',
            fontWeight: 500,
            mb: 1,
            fontSize: { xs: '1.15rem', sm: '1.5rem', md: '1.8rem' },
          }}>
            Let's get in touch
          </Typography>
          
          <Typography variant="body1" sx={{ 
            color: '#333',
            my: { xs: 1, md: 2 },
            fontSize: { xs: '0.8rem', sm: '0.9rem', md: '1rem' },
          }}>
            Contact us with the following details and fill up the form with your information.
          </Typography>

          <Box sx={{ my: { xs: 1.5, md: 3 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1, md: 2 } }}>
              <LocationOnIcon sx={{ color: '#1976d2', mr: 1.5, fontSize: { xs: '1.2rem', md: '1.5rem' } }} />
              <Typography sx={{ fontSize: { xs: '0.8rem', md: '1rem' } }}>Office No 118, SHAIKHA MHARA-AL, QUSAIS BLDG, Al Qusais Second, Dubai, 00000, Dubai</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1, md: 2 } }}>
              <EmailIcon sx={{ color: '#1976d2', mr: 1.5, fontSize: { xs: '1.2rem', md: '1.5rem' } }} />
              <Typography 
                component="a" 
                href="mailto:contact@arcpublishing.org"
                sx={{ 
                  fontSize: { xs: '0.8rem', md: '1rem' },
                  color: '#000',
                  textDecoration: 'none',
                  transition: 'color 0.2s ease',
                  '&:hover': {
                    color: '#1976d2',
                    textDecoration: 'none'
                  }
                }}
              >
                contact@arcpublishing.org
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PhoneIcon sx={{ color: '#1976d2', mr: 1.5, fontSize: { xs: '1.2rem', md: '1.5rem' } }} />
              <Typography 
                component="a" 
                href="tel:+971508370576"
                sx={{ 
                  fontSize: { xs: '0.8rem', md: '1rem' },
                  color: '#000',
                  textDecoration: 'none',
                  transition: 'color 0.2s ease',
                  '&:hover': {
                    color: '#1976d2',
                    textDecoration: 'none'
                  }
                }}
              >
                +971 50 837 0576
              </Typography>
            </Box>
          </Box>

         
        </StyledContactInfo>

        {/* Contact Form Section */}
        <StyledContactFormSection sx={{ 
          p: { xs: '1.7rem 1.6rem', md: '2.3rem 2.2rem' },
          backgroundColor: '#1976d2',
          position: 'relative',
          overflow: 'hidden',
          '&:before': {
            content: '""',
            position: 'absolute',
            width: '26px',
            height: '26px',
            backgroundColor: '#1976d2',
            transform: 'rotate(45deg)',
            bottom: { xs: 'initial', md: '66px' },
            top: { xs: '-13px', md: 'initial' },
            left: { xs: 'initial', md: '-13px' },
            right: { xs: '70px', md: 'initial' },
            display: { xs: 'none', sm: 'block' }, // Hide on smallest mobile
          },
        }}>
          {/* Decorative Circles */}
          <Box sx={{
            position: 'absolute',
            width: '130px',
            height: '130px',
            top: '130px',
            right: '-40px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, transparent 20%, #1976d2)',
            zIndex: 0,
            display: { xs: 'none', md: 'block' },
          }} />
          <Box sx={{
            position: 'absolute',
            width: '80px',
            height: '80px',
            top: '10px',
            right: '30px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, transparent 20%, #1976d2)',
            zIndex: 0,
            display: { xs: 'none', md: 'block' },
          }} />
          
          <Box component="form" onSubmit={handleSubmit} sx={{ position: 'relative', zIndex: 1 }}>
            <Typography variant="h4" sx={{ 
              color: '#fff',
              fontWeight: 500,
              mb: 3,
              fontSize: { xs: '1.15rem', md: '1.5rem' },
            }}>
              Contact us
            </Typography>

            <CustomTextField
              fullWidth
              name="name"
              value={formData.name}
              onChange={handleChange}
              variant="outlined"
              label="Username"
              required
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { 
                    borderColor: 'rgba(255, 255, 255, 0.7)',
                    borderWidth: '1px'
                  },
                  '&:hover fieldset': { 
                    borderColor: 'rgba(255, 255, 255, 0.9)',
                    borderWidth: '1px'
                  },
                  '&.Mui-focused fieldset': { 
                    borderColor: '#fff',
                    borderWidth: '1px'
                  },
                  '& input': { 
                    color: '#fff', 
                    fontSize: { xs: '0.9rem', md: '0.95rem' },
                    '&::placeholder': {
                      color: 'rgba(255, 255, 255, 0.7)',
                      opacity: 1,
                    },
                  },
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '4px',
                },
                '& .MuiInputLabel-root': { 
                  color: 'rgba(255, 255, 255, 0.7)', 
                  fontSize: { xs: '0.8rem', md: '0.9rem' } 
                },
                '& .MuiInputLabel-root.Mui-focused': { 
                  color: '#fff' 
                },
              }}
              InputLabelProps={{
                shrink: true,
                sx: {
                  '&.MuiInputLabel-root': {
                    position: 'static',
                    transform: 'none',
                  }
                }
              }}
              inputProps={{
                style: {
                  color: '#fff',
                },
              }}
            />

            <CustomTextField
              fullWidth
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              variant="outlined"
              label="Email"
              required
              error={!!errors.email}
              helperText={errors.email}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { 
                    borderColor: errors.email ? '#f44336' : 'rgba(255, 255, 255, 0.7)',
                    borderWidth: '1px'
                  },
                  '&:hover fieldset': { 
                    borderColor: errors.email ? '#f44336' : 'rgba(255, 255, 255, 0.9)'
                  },
                  '&.Mui-focused fieldset': { 
                    borderColor: errors.email ? '#f44336' : '#fff'
                  },
                  '& input': { 
                    color: '#fff', 
                    fontSize: { xs: '0.9rem', md: '0.95rem' },
                  },
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '4px',
                },
                '& .MuiInputLabel-root': { 
                  color: errors.email ? '#f44336' : 'rgba(255, 255, 255, 0.7)',
                  fontSize: { xs: '0.8rem', md: '0.9rem' } 
                },
                '& .MuiInputLabel-root.Mui-focused': { 
                  color: errors.email ? '#f44336' : '#fff' 
                },
                '& .MuiFormHelperText-root': {
                  color: '#f44336',
                  marginLeft: 0,
                  marginTop: '3px',
                  fontSize: '0.75rem'
                }
              }}
              InputLabelProps={{
                shrink: true,
                sx: {
                  '&.MuiInputLabel-root': {
                    position: 'static',
                    transform: 'none',
                  }
                }
              }}
              inputProps={{
                style: {
                  color: '#fff',
                },
              }}
            />

            <CustomTextField
              fullWidth
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              variant="outlined"
              label="Phone"
              required
              error={!!errors.phone}
              helperText={errors.phone}
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { 
                    borderColor: errors.phone ? '#f44336' : 'rgba(255, 255, 255, 0.7)',
                    borderWidth: '1px'
                  },
                  '&:hover fieldset': { 
                    borderColor: errors.phone ? '#f44336' : 'rgba(255, 255, 255, 0.9)'
                  },
                  '&.Mui-focused fieldset': { 
                    borderColor: errors.phone ? '#f44336' : '#fff'
                  },
                  '& input': { 
                    color: '#fff', 
                    fontSize: { xs: '0.9rem', md: '0.95rem' },
                  },
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '4px',
                },
                '& .MuiInputLabel-root': { 
                  color: errors.phone ? '#f44336' : 'rgba(255, 255, 255, 0.7)',
                  fontSize: { xs: '0.8rem', md: '0.9rem' } 
                },
                '& .MuiInputLabel-root.Mui-focused': { 
                  color: errors.phone ? '#f44336' : '#fff' 
                },
                '& .MuiFormHelperText-root': {
                  color: '#f44336',
                  marginLeft: 0,
                  marginTop: '3px',
                  fontSize: '0.75rem'
                }
              }}
              InputLabelProps={{
                shrink: true,
                sx: {
                  '&.MuiInputLabel-root': {
                    position: 'static',
                    transform: 'none',
                  }
                }
              }}
              inputProps={{
                style: {
                  color: '#fff',
                },
              }}
            />

            <CustomTextField
              fullWidth
              name="message"
              value={formData.message}
              onChange={handleChange}
              variant="outlined"
              label="Message"
              required
              error={!!errors.message}
              helperText={errors.message}
              multiline
              rows={4}
              InputProps={{
                style: {
                  padding: 0,
                },
              }}
              inputProps={{
                style: {
                  padding: '12px 14px',
                  color: '#fff',
                },
              }}
              sx={{
                mb: 3,
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { 
                    borderColor: errors.message ? '#f44336' : 'rgba(255, 255, 255, 0.7)',
                    borderWidth: '1px'
                  },
                  '&:hover fieldset': { 
                    borderColor: errors.message ? '#f44336' : 'rgba(255, 255, 255, 0.9)'
                  },
                  '&.Mui-focused fieldset': { 
                    borderColor: errors.message ? '#f44336' : '#fff'
                  },
                  '& textarea': { 
                    color: '#fff', 
                    fontSize: { xs: '0.9rem', md: '0.95rem' },
                  },
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '4px',
                },
                '& .MuiInputLabel-root': { 
                  color: errors.message ? '#f44336' : 'rgba(255, 255, 255, 0.7)',
                  fontSize: { xs: '0.8rem', md: '0.9rem' } 
                },
                '& .MuiInputLabel-root.Mui-focused': { 
                  color: errors.message ? '#f44336' : '#fff' 
                },
                '& .MuiFormHelperText-root': {
                  color: '#f44336',
                  marginLeft: 0,
                  marginTop: '3px',
                  fontSize: '0.75rem'
                }
              }}
              InputLabelProps={{
                shrink: true,
                sx: {
                  '&.MuiInputLabel-root': {
                    position: 'static',
                    transform: 'none',
                  }
                }
              }}
             
            />

            <Button
              type="submit"
              variant="contained"
              fullWidth
              sx={{
                backgroundColor: '#fff',
                color: '#1976d2',
                py: 1.5,
                fontWeight: 600,
                fontSize: { xs: '0.8rem', md: '0.95rem' },
                '&:hover': {
                  backgroundColor: '#4D49BF',
                  color: '#fff',
                },
              }}
            >
              Send
            </Button>
          </Box>
        </StyledContactFormSection>
      </StyledContactForm>
    </Box>
  );
};

export default Contact;
